import PropTypes from 'prop-types';
import { useState, useEffect } from 'react';
import { Box, Flex, Text } from 'theme-ui';
import Answer from '~/components/Content/Faq/Answer';
import toSnakeCase from '~/utils/to-snake-case';
import toKebabCase from '~/utils/to-kebab-case';
import MinusIcon from '~/assets/images/icons/minus.svg';
import PlusIcon from '~/assets/images/icons/plus.svg';
import { useCustomAnalytics } from '~/hooks/utils/customAnalytics';
import { useLocation } from '@reach/router';
import useNavigationHeight from '~/context/NavigationContext/useNavigationHeight';

const ContentFaqQuestion = ({ question, setCurrentQuestion, currentQuestion, topic }) => {
  const [open, setOpen] = useState(false);
  const { trackClick } = useCustomAnalytics();
  const { hash } = useLocation();
  const questionId = toKebabCase(
    `${topic.slug.toLowerCase()}-${question.question.question
      .trim()
      .replace(/[^\w\s]/g, '')
      .replace(/\s+/g, ' ')}`
  );
  useEffect(() => {
    if (!currentQuestion || currentQuestion !== question.id) {
      setOpen(false);
    }
  }, [currentQuestion]);

  const sendClickEvent = (e) => {
    const event = {
      locationDetail: 'questions',
      type: 'question',
      title: toSnakeCase(
        `${topic.name} ${question.question.question}`
          .trim()
          .toLowerCase()
          .replace(/[&][/]/gi, 'and')
          .replace(/&nbsp;/, '')
          .replace(/[^\w\s]/gi, '')
      ),
      action: open ? 'close' : 'open',
    };
    trackClick(event);
  };
  useEffect(() => {
    if (hash === `#${questionId}`) {
      setOpen(true);
      setCurrentQuestion(question.id);
    }
  }, [hash, questionId, setCurrentQuestion, question.id]);
  const openQuestion = (e) => {
    setOpen(!open);
    setCurrentQuestion(question.id);
    sendClickEvent(e);
  };

  const { navHeight } = useNavigationHeight();

  return (
    <Box
      onClick={openQuestion}
      role="button"
      id={questionId}
      sx={{
        scrollMarginTop: `calc(${navHeight}px + 4rem)`,
        borderColor: 'primary',
        borderBottom: '1px solid lightgrey',
        pb: '4rem',
        borderTop: 'none',
        '&:not(:last-of-type)': {
          mb: '4rem',
        },
      }}
    >
      <Flex
        sx={{
          cursor: 'pointer',
          flexDirection: 'row',
          justifyContent: 'space-between',
        }}
      >
        <Text
          as="h3"
          sx={{
            pointerEvents: 'none',
            paddingRight: '1rem',
            fontWeight: 'medium',
            fontSize: ['1.3rem', null, '1.6rem'],
          }}
        >
          {question.question.question}
        </Text>
        <Flex
          sx={{
            pointerEvents: 'none',
            alignItems: 'center',
            flexShrink: 0,
            fontSize: '3rem',
          }}
        >
          {open ? <MinusIcon /> : <PlusIcon />}
        </Flex>
      </Flex>
      <Box
        sx={{
          height: 'fit-content',
          overflow: 'hidden',
        }}
      >
        <Answer open={open} answer={question.answer.childMarkdownRemark.html} />
      </Box>
    </Box>
  );
};

ContentFaqQuestion.propTypes = {
  question: PropTypes.shape({
    answer: PropTypes.shape({
      childMarkdownRemark: PropTypes.shape({
        html: PropTypes.string,
      }),
    }),
    question: PropTypes.shape({
      question: PropTypes.string,
    }),
  }),
};

export default ContentFaqQuestion;
